import { supabase } from '@/core/database/supabase';
import type { 
  ProjectZone, 
  ZoneStats, 
  ZoneFilters 
} from '../types/project-zones.types';

export const ProjectZonesService = {
  /**
   * Danh sách khu vực dự án
   */
  async getZones(companyId: string, filters: ZoneFilters = {}): Promise<{ data: ProjectZone[], total: number }> {
    let query = supabase
      .from('project_zones')
      .select(`
        *,
        project:projects(id, name),
        parent_zone:project_zones!parent_zone_id(id, zone_name),
        manager:user_profiles!manager_user_id(id, full_name, avatar_path, role_code)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('zone_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.search) {
      query = query.or(`zone_name.ilike.%${filters.search}%,zone_code.ilike.%${filters.search}%`);
    }

    query = query.order('sort_order', { ascending: true });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ProjectZone[],
      total: count || 0
    };
  },

  /**
   * Chi tiết khu vực
   */
  async getZoneDetail(id: string): Promise<ProjectZone> {
    const { data, error } = await supabase
      .from('project_zones')
      .select(`
        *,
        project:projects(id, name),
        parent_zone:project_zones!parent_zone_id(id, zone_name),
        manager:user_profiles!manager_user_id(id, full_name, avatar_path, role_code)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ProjectZone;
  },

  /**
   * Thống kê tổng quan khu vực
   */
  async getZoneStats(companyId: string, projectId?: string): Promise<ZoneStats> {
    let query = supabase.from('project_zones').select('area_m2, status_code, health_score').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const totalArea = data.reduce((sum, z) => sum + (z.area_m2 || 0), 0);
    const avgHealth = data.length > 0 ? data.reduce((sum, z) => sum + (z.health_score || 0), 0) / data.length : 0;

    return {
      totalZones: data.length,
      totalArea,
      activeWorkOrders: 0, // Mock
      openIssues: 0, // Mock
      avgHealth,
    };
  },

  /**
   * CRUD
   */
  async createZone(data: Partial<ProjectZone>): Promise<ProjectZone> {
    const { data: result, error } = await supabase
      .from('project_zones')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ProjectZone;
  },

  async updateZone(id: string, updates: Partial<ProjectZone>): Promise<void> {
    const { error } = await supabase
      .from('project_zones')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  },

  async deleteZone(id: string): Promise<void> {
    const { error } = await supabase
      .from('project_zones')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
